/**
 * The company behind a listing, including the public spend stat: what it has
 * paid across every listing it has ever run.
 */

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import { type Company } from '@bidtohire/shared';

import { Money } from './Money';
import { CompanyMark } from './ui';

function hostname(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

export function CompanyCard({
  company,
  showLink = true,
}: {
  readonly company: Company;
  readonly showLink?: boolean;
}): ReactNode {
  const details = [
    company.industry,
    company.hqLocation,
    company.sizeBand === undefined ? undefined : `${company.sizeBand} people`,
  ]
    .filter((part): part is string => part !== undefined && part !== '')
    .join(' · ');

  return (
    <section className="card is-padded companycard" aria-label={`About ${company.name}`}>
      <div className="head">
        <CompanyMark company={company} size="md" />
        <div style={{ minWidth: 0 }}>
          <div className="name">{company.name}</div>
          {company.websiteUrl === undefined ? (
            details === '' ? null : <div className="small muted">{details}</div>
          ) : (
            <a
              className="small"
              href={company.websiteUrl}
              rel="nofollow noopener external"
              target="_blank"
            >
              {hostname(company.websiteUrl)}
            </a>
          )}
        </div>
      </div>

      {company.blurb === undefined ? null : <p className="blurb">{company.blurb}</p>}
      {company.websiteUrl !== undefined && details !== '' ? (
        <p className="blurb small muted">{details}</p>
      ) : null}

      <div className="stats">
        <div>
          <span className="eyebrow">Total paid</span>
          <div className="value">
            <Money cents={company.totalSpendCents} />
          </div>
        </div>
        <div>
          <span className="eyebrow">Live listings</span>
          <div className="value mono">{company.activeJobCount}</div>
        </div>
      </div>

      {showLink ? (
        <div style={{ marginTop: 14, fontSize: 13 }}>
          <Link to={`/companies/${company.slug}`}>View company profile →</Link>
        </div>
      ) : null}
    </section>
  );
}
